import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Box, Filter, Loader, Plus, Search, X } from 'lucide-react';
import { useTheme } from '../../../components/common/Theme';
import {
  ENVIRONMENT_OPTIONS,
  PROVIDER_OPTIONS,
  STATUS_OPTIONS,
  TASK_OPTIONS,
  environmentLabel,
  providerLabel,
  statusLabel,
} from './aiModelConstants';
import { logAiModel } from './aiModelLogger';
import { aiModelService } from './aiModelService';
import { AiModelEnvironment, AiModelProvider, AiModelStatus, AiModelTask, AiRegisteredModel } from './aiModelTypes';
import AiModelCard from './AiModelCard';

const AiModelsListing: React.FC = () => {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const navigate = useNavigate();
  const [models, setModels] = useState<AiRegisteredModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [environmentFilter, setEnvironmentFilter] = useState<'all' | AiModelEnvironment>('all');
  const [providerFilter, setProviderFilter] = useState<'all' | AiModelProvider>('all');
  const [taskFilter, setTaskFilter] = useState<'all' | AiModelTask>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | AiModelStatus>('all');
  const [deleteTarget, setDeleteTarget] = useState<AiRegisteredModel | null>(null);

  const loadModels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await aiModelService.list();
      setModels(items);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load AI models';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadModels();
  }, [loadModels]);

  const registeredCount = models.filter((model) => model.status === 'registered').length;
  const productionCount = models.filter((model) => model.environment === 'production').length;

  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return models.filter((model) => {
      if (environmentFilter !== 'all' && model.environment !== environmentFilter) return false;
      if (providerFilter !== 'all' && model.provider !== providerFilter) return false;
      if (taskFilter !== 'all' && model.task !== taskFilter) return false;
      if (statusFilter !== 'all' && model.status !== statusFilter) return false;
      if (!query) return true;
      const haystack = [
        model.name,
        model.code,
        model.endpointId,
        model.description,
        model.version,
        providerLabel(model.provider),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [models, searchQuery, environmentFilter, providerFilter, taskFilter, statusFilter]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    logAiModel('app', 'listing_delete_confirm', { id: target.id, code: target.code });
    setDeleteTarget(null);
    try {
      await aiModelService.remove(target.id);
      setModels((prev) => prev.filter((model) => model.id !== target.id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete model';
      setError(message);
    }
  };

  const selectClass = `px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm ${
    isLight ? 'bg-white border border-neutral-300 text-neutral-900' : 'bg-neutral-800 border border-neutral-700 text-white'
  }`;

  return (
    <div>
      <div className={`border-b ${isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-900/50'}`}>
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className={`text-2xl font-bold flex items-center ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                <Box className="mr-3 text-indigo-400" size={28} />
                AI Models
              </h1>
              <p className={`text-sm mt-1 ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>
                Register deployed inference models for later use in workflows
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/portal/ai-models/register')}
              className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shadow-lg text-sm shrink-0"
            >
              <Plus size={16} className="mr-2" />
              Register model
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
            <div className={`rounded-lg p-3 ${isLight ? 'bg-white border border-neutral-200' : 'bg-neutral-800/50'}`}>
              <div className={`font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{models.length}</div>
              <div className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>Registered models</div>
            </div>
            <div className={`rounded-lg p-3 ${isLight ? 'bg-white border border-neutral-200' : 'bg-neutral-800/50'}`}>
              <div className={`font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{registeredCount}</div>
              <div className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>Active registrations</div>
            </div>
            <div className={`rounded-lg p-3 ${isLight ? 'bg-white border border-neutral-200' : 'bg-neutral-800/50'}`}>
              <div className={`font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{productionCount}</div>
              <div className={`text-xs ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>Production</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-5">
        {error && (
          <div
            className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
              isLight ? 'border-red-200 bg-red-50 text-red-700' : 'border-red-500/20 bg-red-500/10 text-red-300'
            }`}
          >
            {error}
          </div>
        )}
        <div className="flex flex-col xl:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isLight ? 'text-neutral-400' : 'text-gray-400'}`} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search name, code, endpoint, or description..."
              className={`w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm ${
                isLight
                  ? 'bg-white border border-neutral-300 text-neutral-900 placeholder-neutral-400'
                  : 'bg-neutral-800 border border-neutral-700 text-white'
              }`}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Filter size={16} className={isLight ? 'text-neutral-400' : 'text-gray-400'} />
            <select value={environmentFilter} onChange={(e) => setEnvironmentFilter(e.target.value as typeof environmentFilter)} className={selectClass}>
              <option value="all">All environments</option>
              {ENVIRONMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value as typeof providerFilter)} className={selectClass}>
              <option value="all">All providers</option>
              {PROVIDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select value={taskFilter} onChange={(e) => setTaskFilter(e.target.value as typeof taskFilter)} className={selectClass}>
              <option value="all">All tasks</option>
              {TASK_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} className={selectClass}>
              <option value="all">All statuses</option>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <Loader size={40} className="mx-auto mb-3 text-indigo-400 animate-spin" />
            <h3 className={`text-lg font-semibold mb-1 ${isLight ? 'text-neutral-700' : 'text-gray-300'}`}>Loading AI models...</h3>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Box size={40} className={`mx-auto mb-3 ${isLight ? 'text-neutral-300' : 'text-neutral-600'}`} />
            <h3 className={`text-lg font-semibold mb-1 ${isLight ? 'text-neutral-700' : 'text-gray-300'}`}>
              {models.length === 0 ? 'Register your first AI model' : 'No matching models'}
            </h3>
            <p className={`text-sm ${isLight ? 'text-neutral-500' : 'text-gray-500'}`}>
              {models.length === 0
                ? 'Add a deployed inference model so workflows can invoke it later.'
                : 'Try a different search or filter.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((model) => (
              <AiModelCard
                key={model.id}
                model={model}
                isLight={isLight}
                onEdit={(item) => navigate(`/portal/ai-models/${item.id}/edit`)}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
        )}
      </div>

      {deleteTarget &&
        createPortal(
          <div className="fixed inset-0 z-[160] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div
              className={`relative w-full max-w-md rounded-xl shadow-2xl border overflow-hidden ${
                isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'
              }`}
            >
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className={`absolute top-3 right-3 p-1.5 rounded-lg transition-colors ${
                  isLight ? 'text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100' : 'text-gray-400 hover:text-white hover:bg-neutral-800'
                }`}
                aria-label="Close"
              >
                <X size={16} />
              </button>
              <div className="p-6">
                <h3 className={`text-base font-semibold mb-2 ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                  Delete this model?
                </h3>
                <p className={`text-sm leading-relaxed mb-6 ${isLight ? 'text-neutral-600' : 'text-gray-400'}`}>
                  Remove “{deleteTarget.name}” ({deleteTarget.code} {deleteTarget.version} ·{' '}
                  {environmentLabel(deleteTarget.environment)} / {statusLabel(deleteTarget.status)}). This cannot be
                  undone.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(null)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      isLight ? 'text-neutral-600 hover:bg-neutral-100' : 'text-gray-300 hover:bg-neutral-800'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete()}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    Yes, delete
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AiModelsListing;
